import { Link } from 'react-router-dom';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { calculateCreatedAgo, printEventDates } from '../../../components/commonFunc';
import { getHubItemHref, isExternalHubHref, isHubEventType } from '../helper/hubItemHref';
import { ListingCardImage } from './ListingCardImage';
import { KC_COLORS } from '../../knowledge-center/theme';
import {
  excerptFromContent,
  eventLocationLine,
  getHubContentKind,
  parseHubListingTitle,
} from './parseHubListingTitle';

const featuredLinkClassName = 'group block no-underline text-inherit';

export function HubFeatureCard({ firstBlog, to }) {
  const href = to ?? getHubItemHref(firstBlog);
  const kind = getHubContentKind(firstBlog?.postType);
  const { eyebrow, headline, fullTitle } = parseHubListingTitle(
    firstBlog?.title,
    firstBlog?.postType,
  );
  const isEvent = isHubEventType(firstBlog?.postType);
  const excerpt =
    excerptFromContent(firstBlog?.content, 220) ||
    (isEvent ? eventLocationLine(firstBlog) : '');
  const meta = isEvent
    ? printEventDates(firstBlog)
    : `${calculateCreatedAgo(firstBlog)}  |  By Charlee.ai`;

  const content = (
    <article
      className="hub-featured-card relative overflow-hidden rounded-[24px] border bg-white shadow-[0_10px_36px_rgba(10,29,58,0.08)] transition-all duration-300 group-hover:-translate-y-0.5 group-hover:shadow-[0_16px_40px_rgba(10,29,58,0.14)]"
      style={{ borderColor: KC_COLORS.border, textAlign: 'left' }}
    >
      <span
        className="pointer-events-none absolute left-0 top-0 z-10 h-full w-[4px]"
        style={{
          background: 'linear-gradient(180deg, #009BAA 0%, #0A1D3A 100%)',
        }}
        aria-hidden
      />

      <div className="grid grid-cols-1 md:grid-cols-12">
        <div
          className="relative min-h-[240px] overflow-hidden md:col-span-5 md:min-h-[380px]"
          style={{ backgroundColor: KC_COLORS.primaryNavy }}
        >
          <ListingCardImage
            mediaPath={firstBlog?.postMedia}
            title={fullTitle}
            postType={firstBlog?.postType}
            alt={fullTitle}
            showFallbackTitle={false}
            containerClassName="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-3 px-5 py-6 text-center"
            imageClassName="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
          />
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                'linear-gradient(160deg, rgba(10,29,58,0.18) 0%, transparent 42%, rgba(0,155,170,0.18) 100%)',
            }}
            aria-hidden
          />
        </div>

        <div className="flex flex-col justify-center px-5 py-7 md:col-span-7 md:px-8 md:py-9 lg:px-10">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em]"
              style={{ backgroundColor: KC_COLORS.teal, color: KC_COLORS.white }}
            >
              Featured
            </span>
            <span
              className="inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em]"
              style={{
                backgroundColor: KC_COLORS.tealLight,
                color: KC_COLORS.teal,
              }}
            >
              {eyebrow}
            </span>
          </div>

          <h2
            className="m-0 text-[24px] font-semibold leading-[32px] tracking-[0.25px] sm:text-[28px] sm:leading-[36px]"
            style={{
              color: KC_COLORS.primaryNavy,
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
            title={fullTitle}
          >
            {headline}
          </h2>

          {excerpt ? (
            <p
              className="mt-4 mb-0 line-clamp-4 text-[15px] leading-6"
              style={{ color: KC_COLORS.gray600 }}
            >
              {excerpt}
            </p>
          ) : null}

          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <p
              className="m-0 text-[13px] leading-[18px]"
              style={{ color: KC_COLORS.gray600 }}
            >
              {meta}
            </p>
            <span
              className="inline-flex w-fit items-center justify-center gap-1.5 rounded-lg px-5 py-2.5 text-[15px] font-semibold leading-[22px] transition-colors duration-200 group-hover:bg-[#008391]"
              style={{
                backgroundColor: KC_COLORS.teal,
                color: KC_COLORS.white,
              }}
            >
              {kind.cta}
              <ArrowForwardRoundedIcon
                sx={{ fontSize: 18 }}
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </span>
          </div>
        </div>
      </div>
    </article>
  );

  if (!href) {
    return content;
  }

  if (isExternalHubHref(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={featuredLinkClassName}>
        {content}
      </a>
    );
  }

  return (
    <Link to={href} className={featuredLinkClassName}>
      {content}
    </Link>
  );
}
